import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, Card, CardContent, CardActions,
  IconButton, Radio, RadioGroup, FormControl,
  FormLabel, Chip, Alert, Collapse, Select,
  MenuItem, InputLabel, InputAdornment,
} from '@mui/material';
import {
  Add, Delete, ExpandMore, ExpandLess, DragHandle, CheckCircle, Timer, Percent,
} from '@mui/icons-material';
import { CourseTest, MCQQuestion, TestType } from '../../types';
import { contentAPI } from '../../services/api';

interface Props {
  courseId: string;
  testType: TestType;
  test: CourseTest;
  onTestUpdate: (test: CourseTest) => void;
}

interface QuestionEditorProps {
  question: MCQQuestion;
  index: number;
  courseId: string;
  testType: TestType;
  onUpdate: (q: MCQQuestion) => void;
  onDelete: () => void;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({ question, index, courseId, testType, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(true);
  const [saving, setSaving] = useState(false);

  const update = (patch: Partial<MCQQuestion>) => {
    onUpdate({ ...question, ...patch });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await contentAPI.updateQuestion(courseId, testType, question.id, {
        question_text: question.question_text,
        options: question.options,
        correct_answer: question.correct_answer,
        explanation: question.explanation,
        points: question.points,
      });
      onUpdate(res.data.question);
    } catch (e) {
      console.error('Failed to save question', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <Box display="flex" alignItems="center" sx={{ px: 2, py: 1, cursor: 'pointer', bgcolor: 'grey.50' }} onClick={() => setExpanded(!expanded)}>
        <DragHandle sx={{ color: 'text.disabled', mr: 1 }} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Q{index + 1}. {question.question_text || <em style={{ color: '#999' }}>Empty question</em>}
        </Typography>
        <Chip label={`${question.points} pts`} size="small" sx={{ mr: 1 }} />
        <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); onDelete(); }}>
          <Delete fontSize="small" />
        </IconButton>
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </Box>

      <Collapse in={expanded}>
        <CardContent>
          <TextField
            fullWidth
            label="Question Text"
            multiline
            rows={2}
            value={question.question_text}
            onChange={e => update({ question_text: e.target.value })}
            sx={{ mb: 3 }}
          />

          <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{ mb: 1, fontSize: 14 }}>
              Answer Options — select the correct one
            </FormLabel>
            <RadioGroup
              value={question.correct_answer ?? ''}
              onChange={e => update({ correct_answer: parseInt(e.target.value) })}
            >
              {(question.options || ['', '', '', '']).map((opt, i) => (
                <Box key={i} display="flex" alignItems="center" gap={1} mb={1}>
                  <Radio value={i} />
                  <TextField
                    fullWidth
                    size="small"
                    label={`Option ${String.fromCharCode(65 + i)}`}
                    value={opt}
                    onChange={e => {
                      const opts = [...(question.options || ['', '', '', ''])];
                      opts[i] = e.target.value;
                      update({ options: opts });
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: question.correct_answer === i ? 'success.50' : 'transparent',
                      },
                    }}
                  />
                  {question.correct_answer === i && (
                    <CheckCircle color="success" fontSize="small" />
                  )}
                </Box>
              ))}
            </RadioGroup>
          </FormControl>

          <Box display="flex" gap={2}>
            <TextField
              label="Points"
              type="number"
              value={question.points}
              onChange={e => update({ points: parseInt(e.target.value) || 10 })}
              sx={{ width: 120 }}
              inputProps={{ min: 1, max: 100 }}
            />
            <TextField
              fullWidth
              label="Explanation (shown after attempt)"
              value={question.explanation || ''}
              onChange={e => update({ explanation: e.target.value })}
              placeholder="Explain why the correct answer is right..."
            />
          </Box>
        </CardContent>

        <CardActions sx={{ px: 2, pb: 2 }}>
          <Button variant="contained" size="small" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save Question'}
          </Button>
        </CardActions>
      </Collapse>
    </Card>
  );
};

const TestBuilder: React.FC<Props> = ({ courseId, testType, test, onTestUpdate }) => {
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [localTest, setLocalTest] = useState<CourseTest>(test);

  const updateLocalTest = (patch: Partial<CourseTest>) => {
    setLocalTest(prev => ({ ...prev, ...patch }));
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await contentAPI.updateTest(courseId, testType, {
        title: localTest.title,
        time_limit: localTest.time_limit,
        passing_score: localTest.passing_score,
        description: localTest.description,
        max_attempts: localTest.max_attempts,
      });
      const updated = { ...localTest, ...res.data.test };
      setLocalTest(updated);
      onTestUpdate(updated);
    } catch (e) {
      console.error('Failed to save test settings', e);
    } finally {
      setSavingSettings(false);
    }
  };

  const addQuestion = async () => {
    setAddingQuestion(true);
    try {
      const res = await contentAPI.addQuestion(courseId, testType, {
        question_text: '',
        options: ['', '', '', ''],
        correct_answer: 0,
        explanation: '',
        points: testType === 'final' ? 20 : 10,
      });
      const updated = { ...localTest, questions: [...localTest.questions, res.data.question] };
      setLocalTest(updated);
      onTestUpdate(updated);
    } catch (e) {
      console.error('Failed to add question', e);
    } finally {
      setAddingQuestion(false);
    }
  };

  const deleteQuestion = async (questionId: string) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await contentAPI.deleteQuestion(courseId, testType, questionId);
      const updated = { ...localTest, questions: localTest.questions.filter(q => q.id !== questionId) };
      setLocalTest(updated);
      onTestUpdate(updated);
    } catch (e) {
      console.error('Failed to delete question', e);
    }
  };

  const updateQuestion = (updated: MCQQuestion) => {
    const updated_qs = localTest.questions.map(q => q.id === updated.id ? updated : q);
    const updatedTest = { ...localTest, questions: updated_qs };
    setLocalTest(updatedTest);
    onTestUpdate(updatedTest);
  };

  const isMock = testType === 'mock';
  const totalPoints = localTest.questions.reduce((s, q) => s + q.points, 0);

  return (
    <Box>
      {/* Test Type Badge */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Chip
          label={isMock ? 'Practice Quiz' : 'Final Certification Exam'}
          color={isMock ? 'warning' : 'success'}
          size="medium"
        />
        <Typography variant="body2" color="text.secondary">
          {isMock
            ? 'Learners can retake this as many times as needed'
            : 'Learners must pass this to earn their certificate'}
        </Typography>
      </Box>

      {/* Settings Panel */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <Box
          display="flex" alignItems="center" justifyContent="space-between"
          sx={{ px: 2, py: 1.5, cursor: 'pointer', bgcolor: 'grey.50' }}
          onClick={() => setSettingsOpen(!settingsOpen)}
        >
          <Typography variant="subtitle1" fontWeight={600}>Test Settings</Typography>
          {settingsOpen ? <ExpandLess /> : <ExpandMore />}
        </Box>
        <Collapse in={settingsOpen}>
          <CardContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Test Title"
                fullWidth
                value={localTest.title}
                onChange={e => updateLocalTest({ title: e.target.value })}
              />
              <TextField
                label="Instructions / Description"
                fullWidth
                multiline
                rows={2}
                value={localTest.description}
                onChange={e => updateLocalTest({ description: e.target.value })}
                placeholder="Describe what learners should expect..."
              />
              <Box display="flex" gap={2}>
                <TextField
                  label="Time Limit"
                  type="number"
                  value={localTest.time_limit}
                  onChange={e => updateLocalTest({ time_limit: parseInt(e.target.value) || 30 })}
                  InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment>, startAdornment: <Timer sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
                  sx={{ flex: 1 }}
                  inputProps={{ min: 1 }}
                />
                <TextField
                  label="Passing Score"
                  type="number"
                  value={localTest.passing_score}
                  onChange={e => updateLocalTest({ passing_score: parseInt(e.target.value) || 70 })}
                  InputProps={{ endAdornment: <InputAdornment position="end"><Percent fontSize="small" /></InputAdornment> }}
                  sx={{ flex: 1 }}
                  inputProps={{ min: 1, max: 100 }}
                />
                {isMock ? (
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel>Max Attempts</InputLabel>
                    <Select
                      value={localTest.max_attempts === -1 ? '-1' : String(localTest.max_attempts)}
                      label="Max Attempts"
                      onChange={e => updateLocalTest({ max_attempts: parseInt(e.target.value) })}
                    >
                      <MenuItem value="-1">Unlimited</MenuItem>
                      <MenuItem value="1">1</MenuItem>
                      <MenuItem value="2">2</MenuItem>
                      <MenuItem value="3">3</MenuItem>
                      <MenuItem value="5">5</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    label="Max Attempts"
                    value="1"
                    disabled
                    helperText="Final exam: 1 attempt"
                    sx={{ flex: 1 }}
                  />
                )}
              </Box>
            </Box>
          </CardContent>
          <CardActions sx={{ px: 2, pb: 2 }}>
            <Button variant="contained" size="small" onClick={saveSettings} disabled={savingSettings}>
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </Button>
          </CardActions>
        </Collapse>
      </Card>

      {/* Questions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Questions ({localTest.questions.length})
          </Typography>
          {localTest.questions.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Total: {totalPoints} points · Pass at {localTest.passing_score}% ({Math.ceil(totalPoints * localTest.passing_score / 100)} pts)
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={addQuestion}
          disabled={addingQuestion}
          size="small"
        >
          {addingQuestion ? 'Adding...' : 'Add Question'}
        </Button>
      </Box>

      {localTest.questions.length === 0 ? (
        <Alert severity="info">
          No questions yet. Click "Add Question" to start building your {isMock ? 'practice quiz' : 'certification exam'}.
        </Alert>
      ) : (
        localTest.questions.map((q, i) => (
          <QuestionEditor
            key={q.id}
            question={q}
            index={i}
            courseId={courseId}
            testType={testType}
            onUpdate={updateQuestion}
            onDelete={() => deleteQuestion(q.id)}
          />
        ))
      )}

      {/* Add Question button at bottom for easy access after scrolling */}
      {localTest.questions.length > 0 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={addQuestion}
            disabled={addingQuestion}
          >
            {addingQuestion ? 'Adding...' : 'Add Another Question'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TestBuilder;
